import * as client from "../../../client";

export type EffectiveExcerpt = {
  name: string;
  value: string;
  isOverride: boolean;
  sortKey: string | null;
};

/** Merge a limit/deductible's extracted excerpts with the user's manual overrides
 *  and drop removed names. Mirrors the backend `_merge_excerpts`: names join
 *  case-insensitively, the extracted name is kept for display, and the override
 *  supplies the value. */
export const effectiveExcerpts = (
  entry: client.AdvancedLimit | client.AdvancedDeductible
): EffectiveExcerpt[] => {
  const key = (name: string): string => name.toLowerCase();
  const removed = new Set((entry.removed_excerpt_names ?? []).map(key));
  const overrideKeys = new Set(
    Object.keys(entry.manual_excerpt_overrides ?? {}).map(key)
  );
  const sortKeys = new Map(
    Object.entries(entry.excerpt_sort_keys ?? {}).map(([n, k]) => [key(n), k])
  );

  // Extracted names first, then override-only names (manual additions), matching
  // the backend spread order. Re-setting an existing key keeps its position.
  const merged = new Map<string, { name: string; value: string }>();
  for (const [name, value] of Object.entries(entry.direct_excerpts ?? {})) {
    merged.set(key(name), { name, value });
  }
  for (const [name, value] of Object.entries(
    entry.manual_excerpt_overrides ?? {}
  )) {
    merged.set(key(name), { name: merged.get(key(name))?.name ?? name, value });
  }

  const result: EffectiveExcerpt[] = [];
  for (const [k, { name, value }] of merged) {
    // An override wins over a same-name removal, so a case-only rename (remove
    // the old name, re-add it under new casing) keeps the excerpt.
    if (removed.has(k) && !overrideKeys.has(k)) continue;
    result.push({
      name,
      value,
      isOverride: overrideKeys.has(k),
      sortKey: sortKeys.get(k) ?? null,
    });
  }
  return result;
};
